import { useState, useEffect } from 'react';
import { BoardHeader } from './BoardHeader';
import { GroupContainer } from './GroupContainer';
import { ColumnHeader } from './ColumnHeader';
import { ItemRow } from './ItemRow';
import { AddItemRow } from './AddItemRow';
import { PlusIcon, WifiIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useBoardStore } from '../../stores/boardStore';
import { useBoardRealtime } from '../../hooks/useBoardRealtime';
import type { Board } from './demo-data';

interface BoardContainerProps {
  boardId?: string;
  board?: Board;
  onUpdateBoard?: (updates: Partial<Board>) => void;
  onAddGroup?: () => void;
  onAddColumn?: () => void;
  enableRealtime?: boolean;
}

// Connection status indicator component
function ConnectionStatus({
  state,
}: {
  state: 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
}) {
  if (state === 'connected') {
    return (
      <div className="flex items-center gap-1 text-xs text-green-400" title="Connected - Live updates enabled">
        <WifiIcon className="h-3 w-3" />
        <span>Live</span>
      </div>
    );
  }

  if (state === 'connecting' || state === 'reconnecting') {
    return (
      <div className="flex items-center gap-1 text-xs text-yellow-400" title="Connecting...">
        <WifiIcon className="h-3 w-3 animate-pulse" />
        <span>{state === 'reconnecting' ? 'Reconnecting...' : 'Connecting...'}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-xs text-gray-500" title="Disconnected - Changes may not sync">
      <ExclamationTriangleIcon className="h-3 w-3" />
      <span>Offline</span>
    </div>
  );
}

export function BoardContainer({
  boardId,
  board: propBoard,
  onUpdateBoard,
  onAddGroup,
  onAddColumn,
  enableRealtime = true,
}: BoardContainerProps) {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<{
    columnId: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Store state
  const storeBoard = useBoardStore((state) => state.board);
  const setBoard = useBoardStore((state) => state.setBoard);
  const isLoading = useBoardStore((state) => state.isLoading);
  const error = useBoardStore((state) => state.error);
  const addGroupToStore = useBoardStore((state) => state.addGroup);
  const addColumnToStore = useBoardStore((state) => state.addColumn);
  const addItemToStore = useBoardStore((state) => state.addItem);

  // Use prop board or store board
  const board = propBoard || storeBoard;

  // Initialize store with prop board if provided
  useEffect(() => {
    if (propBoard && !storeBoard) {
      setBoard(propBoard);
    }
  }, [propBoard, storeBoard, setBoard]);

  // Realtime sync
  const shouldEnableRealtime = enableRealtime && !!boardId;
  const {
    connectionState,
  } = useBoardRealtime({
    boardId: boardId || '',
    enabled: shouldEnableRealtime,
    onConnectionChange: (state) => {
      console.log('[BoardContainer] Connection state changed:', state);
    },
    onError: (error) => {
      console.error('[BoardContainer] Realtime error:', error);
    },
  });

  // Sort columns by position
  const sortedColumns = board ? [...board.columns].sort((a, b) => a.position - b.position) : [];

  const handleItemSelect = (itemId: string, selected: boolean) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }
      return next;
    });
  };

  const handleColumnSort = (columnId: string, direction: 'asc' | 'desc' | null) => {
    if (direction) {
      setSortConfig({ columnId, direction });
    } else {
      setSortConfig(null);
    }
  };

  const handleAddGroup = () => {
    if (onAddGroup) {
      onAddGroup();
    } else {
      addGroupToStore(`New Group ${(board?.groups.length || 0) + 1}`);
    }
  };

  const handleAddColumn = () => {
    if (onAddColumn) {
      onAddColumn();
    } else {
      addColumnToStore(`Column ${(board?.columns.length || 0) + 1}`, 'text');
    }
  };

  const handleAddItem = (groupId: string, name: string) => {
    addItemToStore(groupId, name);
  };

  // Loading state
  if (isLoading && !board) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading board...</div>
      </div>
    );
  }

  // Error state
  if (error && !board) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-400">{error}</div>
      </div>
    );
  }

  // No board
  if (!board) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">No board selected</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Board header */}
      <div className="relative">
        <BoardHeader
          board={board}
          onToggleFavorite={() => {
            onUpdateBoard?.({ isFavorite: !board.isFavorite });
          }}
        />
        {/* Connection status indicator */}
        {shouldEnableRealtime && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <ConnectionStatus state={connectionState} />
          </div>
        )}
      </div>

      {/* Board content */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-max">
          {/* Column headers */}
          <div className="sticky top-0 z-20 flex bg-white/5 backdrop-blur-xl border-b border-white/10">
            {/* Item name column header */}
            <div className="min-w-[300px] flex items-center h-10 px-3 border-r border-white/10">
              <span className="text-sm font-medium text-gray-400">Item</span>
            </div>

            {/* Dynamic column headers */}
            {sortedColumns.map((column) => (
              <ColumnHeader
                key={column.id}
                id={column.id}
                name={column.name}
                type={column.type}
                width={column.width}
                sortDirection={
                  sortConfig?.columnId === column.id ? sortConfig.direction : null
                }
                onSort={(direction) => handleColumnSort(column.id, direction)}
              />
            ))}

            {/* Add column button */}
            <button
              onClick={handleAddColumn}
              className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-300 hover:bg-white/10"
              title="Add column"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Groups */}
          <div className="p-4">
            {board.groups
              .slice()
              .sort((a, b) => a.position - b.position)
              .map((group) => (
                <GroupContainer
                  key={group.id}
                  id={group.id}
                  name={group.name}
                  color={group.color}
                  itemCount={group.items.length}
                >
                  {/* Items */}
                  {group.items
                    .slice()
                    .sort((a, b) => a.position - b.position)
                    .map((item) => (
                      <ItemRow
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        columnValues={sortedColumns.map((col) => ({
                          columnId: col.id,
                          columnType: col.type,
                          value: item.values[col.id],
                          width: col.width,
                        }))}
                        hasSubitems={item.subitemCount !== undefined && item.subitemCount > 0}
                        subitemCount={item.subitemCount}
                        hasUpdates={item.updateCount !== undefined && item.updateCount > 0}
                        updateCount={item.updateCount}
                        hasFiles={item.fileCount !== undefined && item.fileCount > 0}
                        fileCount={item.fileCount}
                        isSelected={selectedItems.has(item.id)}
                        onSelect={(selected) => handleItemSelect(item.id, selected)}
                      />
                    ))}

                  {/* Add item row */}
                  <AddItemRow
                    groupColor={group.color}
                    onAddItem={(name) => handleAddItem(group.id, name)}
                  />
                </GroupContainer>
              ))}

            {/* Add group button */}
            <button
              onClick={handleAddGroup}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-gray-300 hover:bg-white/10 rounded-md"
            >
              <PlusIcon className="h-4 w-4" />
              Add new group
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
